import { useCallback, useEffect, useState } from "react";
import type { PublicItemDTO } from "@ultradrive/shared-types";
import { getDownloadUrl, getPreviewUrl } from "./api";

// Image / video lightbox for the public viewer. Stripped down from the
// dashboard's FilePreview — drops jobs/series/history/recovery/dev
// panels, keeps just the bits a public viewer needs:
//
//   - <img> / <video> centered, dim backdrop
//   - prev / next nav (← →)
//   - close (Esc / X)
//   - download original (when allowed)
//
// Preview URL is fetched per-asset on open. Sibling preload is omitted
// in v1 — the dashboard's preload pattern relies on cached responses
// from the same fetch hook; cleaner to add later if we see UX gaps.

export function MediaLightbox(props: {
  token: string;
  items: PublicItemDTO[];
  openIndex: number;
  onClose: () => void;
  onChange: (i: number) => void;
  allowDownload: boolean;
}) {
  const item = props.items[props.openIndex];
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [contentType, setContentType] = useState<string>(item?.mimeType ?? "");
  const [downloading, setDownloading] = useState(false);

  const navigate = useCallback(
    (delta: number) => {
      const next = props.openIndex + delta;
      if (next >= 0 && next < props.items.length) {
        props.onChange(next);
      }
    },
    [props],
  );

  useEffect(() => {
    let cancelled = false;
    setPreviewUrl(null);
    setPreviewError(null);
    setContentType(item?.mimeType ?? "");
    if (!item) return;
    void (async () => {
      try {
        const r = await getPreviewUrl(props.token, item.id);
        if (cancelled) return;
        if (r.error) setPreviewError(r.error);
        setPreviewUrl(r.url);
        setContentType(r.contentType);
      } catch (err) {
        if (cancelled) return;
        setPreviewError((err as Error).message ?? "PREVIEW_FAILED");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item, props.token]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
      if (e.key === "ArrowLeft") navigate(-1);
      if (e.key === "ArrowRight") navigate(1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [props, navigate]);

  if (!item) return null;

  const startDownload = async () => {
    if (!props.allowDownload || downloading) return;
    setDownloading(true);
    try {
      const r = await getDownloadUrl(props.token, item.id, { original: true });
      // Open in a new tab for cold (same-origin) URLs and for direct R2
      // (which sets Content-Disposition=attachment) so the browser saves.
      const a = document.createElement("a");
      a.href = r.url;
      a.rel = "noopener";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert((err as Error).message ?? "Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-30 bg-black/85 flex items-center justify-center p-4"
      onClick={props.onClose}
    >
      <div className="absolute top-3 right-3 flex items-center gap-2">
        {props.allowDownload ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void startDownload();
            }}
            disabled={downloading}
            className="rounded-md bg-white/10 hover:bg-white/20 text-white text-sm px-3 py-1.5 backdrop-blur disabled:opacity-50"
          >
            {downloading ? "Preparing…" : "Download"}
          </button>
        ) : null}
        <button
          type="button"
          onClick={props.onClose}
          aria-label="Close"
          className="rounded-md bg-white/10 hover:bg-white/20 text-white text-xl leading-none w-8 h-8 backdrop-blur"
        >
          ×
        </button>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          navigate(-1);
        }}
        disabled={props.openIndex === 0}
        aria-label="Previous"
        className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 text-white w-10 h-10 text-2xl backdrop-blur disabled:opacity-30"
      >
        ‹
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          navigate(1);
        }}
        disabled={props.openIndex === props.items.length - 1}
        aria-label="Next"
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 text-white w-10 h-10 text-2xl backdrop-blur disabled:opacity-30"
      >
        ›
      </button>
      <div
        className="relative max-h-full max-w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {previewError ? (
          <div className="text-white text-center">
            Preview unavailable.
            {props.allowDownload ? (
              <>
                <br />
                <span className="text-sm text-white/70">
                  Use the Download button to fetch the original.
                </span>
              </>
            ) : null}
          </div>
        ) : !previewUrl ? (
          <div className="text-white text-sm">Loading preview…</div>
        ) : item.isVideo ? (
          <video
            src={previewUrl}
            controls
            autoPlay
            className="max-h-[88vh] max-w-[92vw] rounded-md"
          />
        ) : (
          <img
            src={previewUrl}
            alt={item.name}
            className="max-h-[88vh] max-w-[92vw] rounded-md object-contain"
          />
        )}
      </div>
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/80 text-xs">
        {item.name} · {props.openIndex + 1} / {props.items.length}
      </div>
      {void contentType /* contentType reserved for future use */}
    </div>
  );
}
